/**
 * Thin runtime shim around Capacitor plugins. Each helper degrades gracefully
 * to the matching web API when running in a browser (no Capacitor present),
 * so all call sites can stay platform-agnostic.
 *
 *   import { native } from '@/platform/native';
 *   native.haptic('light');   // navigator.vibrate(10) on web, Haptics on iOS/Android
 *   native.lockOrientation('portrait');
 *   native.hideSplash();
 *
 * We only load the @capacitor/* packages when actually running inside a
 * native shell — using dynamic import + try/catch keeps web bundles slim.
 */

type Caps = typeof import('@capacitor/core');
type Haptics = typeof import('@capacitor/haptics');
type StatusBar = typeof import('@capacitor/status-bar');
type SplashScreen = typeof import('@capacitor/splash-screen');
type AppPlugin = typeof import('@capacitor/app');

let core: Caps | null = null;
let haptics: Haptics | null = null;
let statusBar: StatusBar | null = null;
let splash: SplashScreen | null = null;
let appPlugin: AppPlugin | null = null;
let ready: Promise<void> | null = null;

function isLikelyNative(): boolean {
  if (typeof window === 'undefined') return false;
  // Capacitor sets `window.Capacitor.isNativePlatform()` at runtime; before
  // that loads we sniff the URL scheme + UA.
  const w = window as any;
  if (w.Capacitor?.isNativePlatform?.()) return true;
  const url = window.location.href;
  return url.startsWith('capacitor://') || url.startsWith('ionic://');
}

async function loadPlugins(): Promise<void> {
  if (!isLikelyNative()) return;
  try {
    [core, haptics, statusBar, splash, appPlugin] = await Promise.all([
      import('@capacitor/core'),
      import('@capacitor/haptics'),
      import('@capacitor/status-bar'),
      import('@capacitor/splash-screen'),
      import('@capacitor/app'),
    ]);
  } catch (e) {
    console.warn('[native] plugins not available — running in web mode', e);
  }
}

export const native = {
  /** Lazily import the @capacitor/* plugins. Safe to call multiple times. */
  init(): Promise<void> {
    if (!ready) ready = loadPlugins();
    return ready;
  },

  /** True when running inside the Capacitor native shell. */
  isNative(): boolean {
    return !!core?.Capacitor?.isNativePlatform?.();
  },

  /** Platform-portable haptic feedback. */
  haptic(kind: 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error' = 'light'): void {
    // Web fallback: cheap navigator.vibrate. Capacitor when available.
    if (haptics) {
      try {
        if (kind === 'success' || kind === 'warning' || kind === 'error') {
          void haptics.Haptics.notification({ type: kind as any });
        } else {
          void haptics.Haptics.impact({ style: kind as any });
        }
        return;
      } catch { /* fall through */ }
    }
    try {
      const ms = kind === 'heavy' ? 30 : kind === 'medium' ? 18 : 10;
      navigator.vibrate?.(ms);
    } catch { /* */ }
  },

  /** Dismiss the launch splash. Call once after the first paint. Awaits plugin
   * load first — with launchAutoHide=false a no-op here would leave the native
   * splash stuck on screen. */
  hideSplash(): void {
    void this.init().then(() => {
      void splash?.SplashScreen.hide({ fadeOutDuration: 250 }).catch(() => { /* */ });
    });
  },

  /** Style the system status bar to match our dark theme. */
  styleStatusBar(): void {
    if (!statusBar) return;
    try {
      void statusBar.StatusBar.setStyle({ style: statusBar.Style.Dark });
      void statusBar.StatusBar.setBackgroundColor({ color: '#050314' });
    } catch { /* */ }
  },

  /** Handle the Android hardware back button. Returns the unsub function. */
  onBackButton(handler: () => boolean | void): () => void {
    if (!appPlugin) return () => {};
    let listener: { remove: () => Promise<void> } | undefined;
    void appPlugin.App.addListener('backButton', () => {
      const handled = handler();
      if (handled === false && appPlugin) void appPlugin.App.exitApp();
    }).then((l) => { listener = l; });
    return () => { void listener?.remove(); };
  },

  /** Pause/resume hooks for the app lifecycle (e.g. mute audio when backgrounded). */
  onPause(cb: () => void): () => void {
    if (!appPlugin) return () => {};
    let listener: { remove: () => Promise<void> } | undefined;
    void appPlugin.App.addListener('pause', cb).then((l) => { listener = l; });
    return () => { void listener?.remove(); };
  },
  onResume(cb: () => void): () => void {
    if (!appPlugin) return () => {};
    let listener: { remove: () => Promise<void> } | undefined;
    void appPlugin.App.addListener('resume', cb).then((l) => { listener = l; });
    return () => { void listener?.remove(); };
  },
};
